import * as FluxUtils from 'flux/utils';
import * as Lockr from 'lockr';

import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { AppConfigurationStore, AppConfiguration } from './AppConfigurationStore';
import { AppLanguageHelper } from '../utils/AppLanguageHelper';

import utils = egr.wcf.utils;

export type SidebarPosition = 'right' | 'left';
export type ImageFormat = 'JPG' | 'PNG' | 'PNG*';
export type MeasurementUnitType = 'm' | 'cm' | 'mm' | 'feet' | 'inch';
export type SortType = 'name-up' | 'name-down' | 'date-up' | 'date-down';

// number/boolean doesn't work, use string always
export interface PersistentStoreState {
    language: string;
    sidebarPosition: SidebarPosition;
    imageFormat: ImageFormat;
    autoSaveId: string | undefined;

    dataLanguage: string;
    unitOfMeasurement: MeasurementUnitType;
    articleListPrices: 'show' | 'hide';
    currency: string;
    wall3d: boolean;
    projectSort: SortType;

    cookieAccepted: 'true' | 'false';
    frontWalls: 'invisible' | 'visible';
}

class PersistentStoreClass extends FluxUtils.Store<unknown> {
    public getImageExtension(): string {
        const state: PersistentStoreState = this.getState();
        if (state.imageFormat === 'JPG') {
            return 'jpg';
        }
        return 'png';
    }

    public getState(): PersistentStoreState {
        return {
            sidebarPosition:
                (AppConfigurationStore.getState().sidebarMoveable ? null : this.getInitialSidebarPosition()) ||
                Lockr.get('sidebarPosition') as SidebarPosition ||
                this.getInitialSidebarPosition(),
            // tslint:disable-next-line:max-line-length
            language: AppLanguageHelper.getURLParameter('language') || Lockr.get('language') || AppLanguageHelper.getValidLanguage(AppConfiguration.FallbackLanguage),
            imageFormat:  Lockr.get('image-format') as ImageFormat || 'PNG',
            autoSaveId: Lockr.get('autoSaveId'),

            // tslint:disable-next-line:max-line-length
            dataLanguage: AppLanguageHelper.getURLParameter('language') || Lockr.get('dataLanguage') as string || (navigator.language || (navigator as any).userLanguage).substr(0, 2),
            unitOfMeasurement: Lockr.get('unitOfMeasurement') as MeasurementUnitType || 'm',
            articleListPrices: Lockr.get('articleListPrices') as 'show' | 'hide' || 'show',
            currency: Lockr.get('currency') as string || 'EUR',
            projectSort: Lockr.get('projectSort') as SortType || 'date-down',
            wall3d: JSON.parse(Lockr.get('wall3D', JSON.stringify(true)) as string),

            cookieAccepted: Lockr.get('cookieAccepted') as 'true' | 'false' || 'false',
            frontWalls: Lockr.get('frontWalls') as 'invisible' | 'visible' || 'invisible'
        };
    }

    public shouldStartWithProjectManagement(): boolean {
        // Do not show the project management view on the first start (no `autoSaveId` key)
        return utils.string.isNullOrEmpty(this.getState().autoSaveId) && Lockr.get('autoSaveId') != null;
    }

    public __onDispatch(action: DispatcherBaseAction): void {
        switch (action.actionType) {
            case ActionConstants.ACCEPT_COOKIE:
                Lockr.set('cookieAccepted', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_PROJECTS_SORT:
                Lockr.set('projectSort', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_IMAGE_FORMAT:
                Lockr.set('image-format', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_IMAGE_TRANSPARENT:
                Lockr.set('image-transparent', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_MEASUREMENT:
                Lockr.set('unitOfMeasurement', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_PRICE_VISIBLITY:
                Lockr.set('articleListPrices', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_DATA_LANGUAGE:
                Lockr.set('dataLanguage', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_CURRENCY:
                Lockr.set('currency', action.value);
                this.__emitChange();
                break;
            case ActionConstants.HIDE_BANNER:
                Lockr.set('showBanner', 'false');
                this.__emitChange();
                break;
            case ActionConstants.SET_LANGUAGE:
                Lockr.set('language', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_SIDEBAR_POSITION:
                if (AppConfigurationStore.getState().sidebarMoveable) {
                    Lockr.set('sidebarPosition', action.value);
                    this.__emitChange();
                }
                break;
            case ActionConstants.SET_AUTOSAVE_ID:
                Lockr.set('autoSaveId', action.value);
                this.__emitChange();
                break;
            case ActionConstants.SET_3D_WALLMODE:
                Lockr.set('wall3D', JSON.stringify(action.value));
                this.__emitChange();
                break;
            case ActionConstants.SET_FRONT_WALLS:
                Lockr.set('frontWalls', action.value);
                this.__emitChange();
                break;
            default:
        }
    }

    protected getInitialSidebarPosition(): SidebarPosition {
        if (AppConfigurationStore.getState().inApp) {
            return 'right';
        }
        return 'left';
    }
}

export let PersistentStore: PersistentStoreClass = new PersistentStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/PersistentStore.ts